import { useState, useCallback, useEffect } from 'react'
import { Icon } from '@iconify/react'
import type { RouletteConfig, RouletteConfigInput } from '@/lib/roulette'
import styles from './CampanhasPage.module.css'
import type { RoletaSaveTask } from './roletaSaveTask'

const POSITIONS_MIN = 2
const POSITIONS_MAX = 16

function clampPositions(n: number): number {
  if (!Number.isFinite(n)) return POSITIONS_MIN
  return Math.min(POSITIONS_MAX, Math.max(POSITIONS_MIN, Math.round(n)))
}

function resolveIsActive(config: RouletteConfig | null): boolean {
  if (!config) return true
  return config.is_active !== false
}

export interface RoletaConfigFormProps {
  config: RouletteConfig | null
  onSave: (input: RouletteConfigInput) => Promise<void>
  onSaveAll: () => Promise<boolean>
  onSaveStateChange: (state: RoletaSaveTask | null) => void
  saveDisabled?: boolean
  isSaving?: boolean
  errorMessage?: string | null
  /** Chamado sempre que o número de posições no formulário mudar (valor já limitado entre 2 e 16). */
  onPositionsChange?: (positions: number) => void
}

export function RoletaConfigForm({
  config,
  onSave,
  onSaveAll,
  onSaveStateChange,
  saveDisabled = false,
  isSaving = false,
  errorMessage = null,
  onPositionsChange,
}: RoletaConfigFormProps) {
  const [isActive, setIsActive] = useState<boolean>(() => resolveIsActive(config))
  const [positions, setPositions] = useState<number>(config?.positions ?? 8)
  const [spinsPerShare, setSpinsPerShare] = useState<number>(config?.spins_per_share ?? 1)
  const [amountPerSpin, setAmountPerSpin] = useState<string>(
    config?.amount_per_spin != null ? String(config.amount_per_spin) : ''
  )
  const [spinsPerAmount, setSpinsPerAmount] = useState<number>(
    config?.spins_per_amount ?? 1
  )
  const [localError, setLocalError] = useState<string | null>(null)
  const [isConfigExpanded, setIsConfigExpanded] = useState(true)

  useEffect(() => {
    onPositionsChange?.(positions)
  }, [positions, onPositionsChange])

  const validate = useCallback(() => {
    setLocalError(null)
    const amount = Number(amountPerSpin.trim().replace(',', '.'))
    if (!Number.isFinite(amount) || amount < 0) {
      setLocalError('O valor por bloco deve ser um número maior ou igual a zero.')
      setIsConfigExpanded(true)
      return false
    }
    return true
  }, [amountPerSpin])

  const handleSaveClick = useCallback(async () => {
    if (!validate()) return false
    try {
      await onSave({
        is_active: isActive,
        positions,
        spins_per_share: spinsPerShare,
        amount_per_spin:
          amountPerSpin.trim() === ''
            ? null
            : Number(amountPerSpin.trim().replace(',', '.')),
        spins_per_amount: spinsPerAmount,
      })
      return true
    } catch {
      setLocalError('Não foi possível salvar a configuração. Tente novamente.')
      return false
    }
  }, [
    onSave,
    validate,
    isActive,
    positions,
    spinsPerShare,
    amountPerSpin,
    spinsPerAmount,
  ])

  const hasChanges =
    !config ||
    isActive !== resolveIsActive(config) ||
    positions !== config.positions ||
    spinsPerShare !== config.spins_per_share ||
    spinsPerAmount !== config.spins_per_amount ||
    (amountPerSpin.trim() === ''
      ? null
      : Number(amountPerSpin.trim().replace(',', '.'))) !==
      (config.amount_per_spin == null ? null : Number(config.amount_per_spin))

  useEffect(() => {
    onSaveStateChange({ hasChanges, saving: isSaving, save: handleSaveClick, validate })
  }, [hasChanges, isSaving, handleSaveClick, validate, onSaveStateChange])
  useEffect(() => () => onSaveStateChange(null), [onSaveStateChange])

  const displayError = errorMessage ?? localError
  const toggleDisabled = isSaving

  const configSummary = `${isActive ? 'Ativa' : 'Desativada'} · ${positions} posição(ões) · ${spinsPerShare} giro(s) ao compartilhar${
    amountPerSpin.trim() !== '' && spinsPerAmount > 0
      ? ` · ${spinsPerAmount} giro(s) a cada R$ ${amountPerSpin}`
      : ''
  }`

  return (
    <section
      className={`${styles.panelCard} ${!isConfigExpanded ? styles.panelCardCollapsed : ''}`}
      aria-labelledby="roleta-config-title"
    >
      <div className={styles.panelHeaderWithCollapse}>
        <div className={styles.panelHeaderMain}>
          <h3 id="roleta-config-title" className={styles.sectionTitle}>
            Configurações da roleta
          </h3>
          <p className={styles.sectionSubtitle}>
            {isConfigExpanded
              ? 'Defina a quantidade de posições e as regras de giros (compartilhamento e valor pago).'
              : `${configSummary}. Abra as configurações para editar.`}
          </p>
        </div>
        <div className={styles.panelHeaderActions}>
          {!isConfigExpanded ? (
            <button
              type="button"
              className={styles.primaryButton}
              disabled={saveDisabled}
              onClick={() => void onSaveAll()}
            >
              {isSaving ? 'Salvando...' : 'Salvar roleta'}
            </button>
          ) : null}
          <button
            type="button"
            className={styles.panelCollapseToggle}
            onClick={() => setIsConfigExpanded((expanded) => !expanded)}
            aria-expanded={isConfigExpanded}
            aria-controls="roleta-config-panel"
            aria-label={
              isConfigExpanded
                ? 'Recolher configurações da roleta'
                : 'Abrir configurações da roleta'
            }
          >
            <Icon icon="mdi:cog-outline" aria-hidden />
            <span>Configurações da roleta</span>
            <Icon
              icon={isConfigExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'}
              aria-hidden
            />
          </button>
        </div>
      </div>

      {displayError ? (
        <div className={styles.errorBanner} role="alert">
          {displayError}
        </div>
      ) : null}

      {isConfigExpanded ? (
        <fieldset
          id="roleta-config-panel"
          className={styles.panelFields}
          disabled={isSaving}
        >
          <div
            className={styles.formSection}
            style={{ marginBottom: 'var(--spacing-lg)' }}
          >
            <div className={styles.field}>
              <label
                htmlFor="roleta-is-active"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                  cursor: toggleDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <input
                  id="roleta-is-active"
                  type="checkbox"
                  checked={isActive}
                  disabled={toggleDisabled}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <span style={{ fontWeight: 600 }}>Roleta ativa</span>
              </label>
              <p
                style={{
                  margin: 'var(--spacing-xs) 0 0',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                }}
              >
                Quando desativada, compradores não veem a roleta na campanha nem no widget. A
                configuração e os prêmios permanecem salvos para reativar depois.
              </p>
            </div>
            {!isActive && (
              <div
                className={styles.errorBanner}
                role="status"
                style={{ marginTop: 'var(--spacing-sm)' }}
              >
                A roleta está desativada. Compradores não verão nem usarão a roleta até
                reativar.
              </div>
            )}
          </div>

          {config ? (
            <div
              className={styles.formSection}
              style={{ marginBottom: 'var(--spacing-lg)' }}
            >
              <h4>Resumo salvo no servidor</h4>
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {resolveIsActive(config) ? 'Status: ativa' : 'Status: desativada'} |{' '}
                {config.positions} posições | {config.spins_per_share} giro(s) ao
                compartilhar
                {config.amount_per_spin != null && config.spins_per_amount > 0
                  ? ` | ${config.spins_per_amount} giro(s) a cada R$ ${config.amount_per_spin}`
                  : ''}
              </p>
            </div>
          ) : null}

          <div className={styles.formCard}>
            <div className={styles.formSection}>
              <h4>Slots e giros</h4>
              <div className={styles.formGrid}>
                <div className={styles.field}>
                  <label htmlFor="roleta-positions">Posições da roleta (2 a 16)</label>
                  <input
                    id="roleta-positions"
                    type="number"
                    min={POSITIONS_MIN}
                    max={POSITIONS_MAX}
                    value={positions}
                    onChange={(e) => {
                      const raw = Number.parseInt(e.target.value, 10)
                      const next = clampPositions(Number.isNaN(raw) ? POSITIONS_MIN : raw)
                      setPositions(next)
                    }}
                    className={styles.input}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="roleta-spins-per-share">Giros ao compartilhar</label>
                  <input
                    id="roleta-spins-per-share"
                    type="number"
                    min={0}
                    value={spinsPerShare}
                    onChange={(e) =>
                      setSpinsPerShare(
                        Math.max(0, Number.parseInt(e.target.value, 10) || 0)
                      )
                    }
                    className={styles.input}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="roleta-amount-per-spin">
                    Valor em R$ por bloco (opcional)
                  </label>
                  <input
                    id="roleta-amount-per-spin"
                    type="text"
                    inputMode="decimal"
                    placeholder="Ex.: 50"
                    value={amountPerSpin}
                    onChange={(e) => setAmountPerSpin(e.target.value)}
                    className={styles.input}
                  />
                </div>
                <div className={styles.field}>
                  <label htmlFor="roleta-spins-per-amount">Giros por bloco pago</label>
                  <input
                    id="roleta-spins-per-amount"
                    type="number"
                    min={0}
                    value={spinsPerAmount}
                    onChange={(e) =>
                      setSpinsPerAmount(
                        Math.max(0, Number.parseInt(e.target.value, 10) || 0)
                      )
                    }
                    className={styles.input}
                  />
                </div>
              </div>
            </div>

            <div className={styles.formFooter}>
              <button
                type="button"
                className={styles.primaryButton}
                disabled={saveDisabled}
                onClick={() => void onSaveAll()}
              >
                {isSaving ? 'Salvando...' : 'Salvar roleta'}
              </button>
            </div>
          </div>
        </fieldset>
      ) : null}
    </section>
  )
}
